import {
  ChangeDetectionStrategy,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute } from '@angular/router';
import getDay from 'date-fns/getDay';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { makeDepartmentPermissionsComplete } from 'server/helpers/permissions/permissions';
import { Jobs } from 'server/shared/collections/jobs';
import { checkIfRested } from 'server/shared/helpers/eligibilityChecks';
import { formatUserName } from 'server/shared/helpers/user/formatUserName';
import { AvailabilityListUser } from 'server/shared/models/availabilityListUser';
import { AvailabilityStatusEnum } from 'server/shared/models/availabilityStatusEnum';
import { Customer } from 'server/shared/models/customer';
import { InviteeStatusEnum } from 'server/shared/models/jobInviteeStatusEnum';
import { JobStatusEnum } from 'server/shared/models/jobStatusEnum';
import { Shift } from 'server/shared/models/shift';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { DispoUser, PoolerUser, User } from 'server/shared/models/user';
import { UserAvailability } from 'server/shared/models/userAvailability';
import { getWeekDayEnumByIndex } from 'server/shared/models/weekday.enum';
import { SubscriptionService } from 'src/app/shared/subscription.service';
import { rxFilterIsNotEmpty } from 'src/helpers/filterIsNotEmpty';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { getRestWarningString } from '../../../../helpers/getRestWarning';
import { CustomerService } from '../../../services/customer.service';
import { PermissionsService } from '../../../services/permissions.service';
import { PoolerService } from '../../../services/pooler/pooler.service';
import { TranslationService } from '../../../services/translation.service';
import { PreviousJobsLogSheetComponent } from '../../../shared/previous-jobs-log-sheet/previous-jobs-log-sheet.component';
import { AvailabilitySelectionDialogComponent } from '../availability-selection-dialog/availability-selection-dialog.component';
import {
  AvailabilityManagementService,
  DepartmentGroup,
} from '../service/availability-management.service';
import {
  AvailabilitySelectionService,
  SelectedAvailabilityData,
} from '../service/availability-selection.service';

export type MonthGroup = {
  monthIndex: number;
  days: GroupedDay[];
};

export type WeekGroup = {
  isoWeek: number;
  days: GroupedDay[];
};
export type GroupedDay = {
  unixDate: number;
  totalIndex: number;
};

@Component({
  selector: 'app-availabilities-user-list',
  templateUrl: './availabilities-user-list.component.html',
  styleUrls: [
    './availabilities-user-list.component.scss',
    '../../../shared/modules/custom-date-table/date-table-shared-styles.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvailabilitiesUserListComponent implements OnInit, OnDestroy {
  // Input as observable
  private loggedInUserInput$ = new BehaviorSubject<DispoUser | undefined>(
    undefined,
  );
  loggedInUser$ = this.loggedInUserInput$.pipe(rxFilterIsNotEmpty());

  @Input() set loggedInUser(loggedInUser: DispoUser | undefined) {
    this.loggedInUserInput$.next(loggedInUser);
  }
  get loggedInUser() {
    return this.loggedInUserInput$.value;
  }

  shiftSelectionMenuOptions$ = new BehaviorSubject<
    {
      departmentGroup: DepartmentGroup;
      user: AvailabilityListUser;
      shiftEnum: ShiftEnum;
      userAvailability: UserAvailability;
      shift: Shift;
    }[]
  >([]);

  multiCreationSelection$ =
    this.availabilityManageService.multiCreationSelection$;

  getSelectionMapKey = this.availabilityManageService.getSelectionMapKey;
  i18n = this.translationService.getTranslations('availability');

  shiftEnum = ShiftEnum;

  pendingInvitations$ = new BehaviorSubject<{ [key: string]: boolean }>({});

  onlyOneDepartmentVisible: boolean = false;

  // Customer as BehaviorSubject for .value access
  customer$ = new BehaviorSubject<Customer | undefined>(undefined);

  timeout?: NodeJS.Timeout;

  // Observables from service
  currentCollapsedDepartmentId$ =
    this.availabilityManagementService.currentCollapsedDepartmentId$;
  displayListItemsCount$ =
    this.availabilityManagementService.displayListItemsCount$;
  selectedDayIndex$ = this.availabilityManagementService.selectedDayIndex$;

  private destroyed$ = new Subject<void>();
  private pendingInvitationsTracker?: Tracker.Computation;

  mappedDepartmentNames$ =
    this.availabilityManagementService.allOrganisations$.pipe(
      map(
        (organisations) =>
          new Map(
            organisations.flatMap((organisation) =>
              organisation.departments.map(
                (department) =>
                  [
                    department.id,
                    `${department.name} - ${organisation.name}`,
                  ] as [string, string],
              ),
            ),
          ),
      ),
    );

  days$ = this.availabilityManagementService.calendarTableHelper$.pipe(
    map((calendarTableHelper) => calendarTableHelper.days),
  );

  employmentUsersReady$ = this.subscriptionService.employmentUsersReady$;

  constructor(
    private ngZone: NgZone,
    private translationService: TranslationService,
    private poolerService: PoolerService,
    private customerService: CustomerService,
    private route: ActivatedRoute,
    private bottomSheet: MatBottomSheet,
    public availabilityManagementService: AvailabilityManagementService,
    public availabilityManageService: AvailabilitySelectionService,
    private subscriptionService: SubscriptionService,
    private permissionsService: PermissionsService,
    private dialog: MatDialog,
  ) {
    // Subscribe to user organisation groups
    this.availabilityManagementService.departmentGroups$
      .pipe(
        takeUntil(this.destroyed$),
        map((groups) =>
          groups.length === 1 ? groups[0]?.department.id : undefined,
        ),
        distinctUntilChanged(),
        rxNgZoneRun(this.ngZone),
        tap((departmentId) => {
          if (departmentId) {
            this.accordionToggled(false, departmentId);
          }
        }),
      )
      .subscribe();

    // Subscribe to display list items count - no longer needed as service handles this directly
  }

  accordionToggled(isCollapsed: boolean, departmentId: string) {
    this.availabilityManagementService.accordionToggled(
      isCollapsed,
      departmentId,
    );
  }

  async ngOnInit(): Promise<void> {
    // Subscribe to customer from service
    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.customer$.next(customer);
      });
    this.pendingInvitationsTracker = Tracker.autorun(() => {
      setTimeout(() => {
        Jobs.find({ status: JobStatusEnum.OPEN })
          .pipe(debounceTime(10))
          .subscribe((jobs) => {
            const pendingInvitations: { [key: string]: boolean } = {};
            jobs.forEach((job) => {
              if (job.invitees) {
                for (let i = 0; i < job.invitees.length; i++) {
                  if (
                    job.invitees[i].customer.id !== this.customer$.value?._id ||
                    job.invitees[i].status === InviteeStatusEnum.BOOKED
                  )
                    continue;
                  const userId = job.invitees[i].userId;
                  if (!userId) continue;
                  for (let index = 0; index < job.shifts.length; index++) {
                    const shift = this.shiftEnum[job.shifts[index].shift];
                    pendingInvitations[
                      userId + job.date + job.department.id + shift
                    ] = true;
                  }
                }
              }
            });
            this.pendingInvitations$.next(pendingInvitations);
          });
      }, 0);
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.pendingInvitationsTracker) {
      this.pendingInvitationsTracker.stop();
    }
  }

  async clickAvailability(
    departmentGroup: DepartmentGroup,
    user: AvailabilityListUser,
    shiftEnum: ShiftEnum,
    userAvailability: UserAvailability,
    shiftMenuTrigger?: MatMenuTrigger,
    specificShift?: Shift,
  ) {
    const weekDay = getWeekDayEnumByIndex(
      getDay(new Date(userAvailability.date)),
    );
    const shiftOptions = departmentGroup.department.shifts.filter(
      (s) =>
        s.shift === shiftEnum &&
        (!s.specificWeekDays ||
          s.specificWeekDays.length === 0 ||
          s.specificWeekDays.includes(weekDay)),
    );

    let shiftCurrentlySelected =
      this.multiCreationSelection$.value?.selection?.has(
        this.availabilityManageService.getSelectionMapKey(
          user,
          shiftEnum,
          userAvailability.date,
        ),
      );
    // check if user must select shift in menu first
    if (shiftOptions.length > 1 && !specificShift && !shiftCurrentlySelected) {
      this.shiftSelectionMenuOptions$.next(
        shiftOptions.map((shift) => ({
          departmentGroup,
          user,
          shiftEnum,
          userAvailability,
          shift,
        })),
      );
      return;
    } else {
      // I tried to work around this, but i cant make the trigger dynamic without adding a separate element for it that is not clicked
      shiftMenuTrigger?.closeMenu();
    }
    let restStatus = checkIfRested({
      user: user,
      jobDate: userAvailability.date,
      jobShifts: [specificShift || shiftOptions[0]],
      requiredPause: 11,
    });
    let restWarning = '';

    if (!restStatus.hasRested) {
      restWarning = getRestWarningString(restStatus, this.i18n!);
    }

    if (!shiftCurrentlySelected && (userAvailability.note || restWarning)) {
      const dialogRef = this.dialog.open(AvailabilitySelectionDialogComponent, {
        data: {
          personName: user.firstName + ' ' + user.lastName,
          restWarning,
          note: userAvailability.note,
        },
      });
      dialogRef
        .afterClosed()
        .subscribe(async (result: { confirmed: boolean }) => {
          if (result && result.confirmed) {
            this.selectAvailability(
              departmentGroup,
              user,
              shiftEnum,
              userAvailability,
              specificShift,
            );
          }
        });
    } else {
      this.selectAvailability(
        departmentGroup,
        user,
        shiftEnum,
        userAvailability,
        specificShift,
      );
    }
  }

  async selectAvailability(
    departmentGroup: DepartmentGroup,
    user: AvailabilityListUser,
    shiftEnum: ShiftEnum,
    userAvailability: UserAvailability,
    specificShift?: Shift,
  ) {
    if (
      !this.permissionsService.userHasDepartmentWritePermission(
        departmentGroup.department.id,
        makeDepartmentPermissionsComplete(
          this.loggedInUser?.dispoPermissions?.departments,
        ),
      )
    ) {
      return;
    }
    if (!userAvailability?.shifts?.[shiftEnum]) return;
    const { date, note } = userAvailability;
    const availabilityStatus: AvailabilityStatusEnum =
      // @ts-ignore
      userAvailability.shifts[shiftEnum.toString()];
    if (availabilityStatus === AvailabilityStatusEnum.AVAILABLE) {
      if (departmentGroup.hasShifts?.[shiftEnum]) {
        const selectedAvailabilityData: SelectedAvailabilityData = {
          department: departmentGroup.department,
          user,
          shiftEnum,
          specificShiftId: specificShift?.id,
          date: date,
        };
        if (note) {
          selectedAvailabilityData.note = note;
        }
        this.availabilityManageService
          .selectAvailability(selectedAvailabilityData)
          .then();
      }
    }
  }

  displayMoreEntries() {
    this.availabilityManagementService.displayMoreEntries();
  }

  selectDay(index: number) {
    this.availabilityManagementService.selectDay(index);
  }

  getTotalAvailableCountPerDepartment(
    user: PoolerUser,
    departmentHasShifts: Record<ShiftEnum, boolean>,
  ): number {
    let count = 0;

    for (let availability of user.availabilities) {
      if (availability.isAvailable && availability.shifts) {
        for (const [key, value] of Object.entries(availability.shifts)) {
          if (
            departmentHasShifts.hasOwnProperty(key) &&
            // @ts-ignore
            departmentHasShifts[key] &&
            value === 'AVAILABLE'
          ) {
            count++;
          }
        }
      }
    }
    return count;
  }

  getTotalDepartmentBookedCount(user: PoolerUser, departmentId: string) {
    let count = 0;
    for (let availability of user.availabilities) {
      if (
        availability.bookedJobs &&
        Object.keys(availability.bookedJobs).length
      ) {
        const bookedJobs = Object.keys(availability.bookedJobs);
        for (let key of bookedJobs) {
          // @ts-ignore
          let job = availability.bookedJobs[key];
          if (
            job.departmentId === departmentId &&
            (job.inviteeCustomerId === this.customer$.value?._id ||
              job.organisationOwnerCustomerIds?.includes(
                this.customer$.value?._id,
              ) ||
              job.creatorCustomerId === this.customer$.value?._id)
          ) {
            count++;
            break;
          }
        }
      }
    }
    return count;
  }

  getTotalBookedCount(user: PoolerUser) {
    let count = 0;
    for (let availability of user.availabilities) {
      if (
        availability.bookedJobs &&
        Object.keys(availability.bookedJobs).length
      ) {
        const bookedJobs = Object.keys(availability.bookedJobs);
        for (let key of bookedJobs) {
          // @ts-ignore
          let job = availability.bookedJobs[key];
          if (
            job.inviteeCustomerId === this.customer$.value?._id ||
            job.organisationOwnerCustomerIds?.includes(
              this.customer$.value?._id,
            ) ||
            job.creatorCustomerId === this.customer$.value?._id
          ) {
            count++;
            break;
          }
        }
      }
    }
    return count;
  }

  toggleDetails(user: User) {
    this.ngZone.run(() => {
      user.detailsCollapsed = !user.detailsCollapsed;
    });
  }

  public openPreviousJobsSheet(user: AvailabilityListUser): void {
    const currentCollapsedDepartmentId =
      this.availabilityManagementService.currentCollapsedDepartmentId$.value;
    if (!user || !currentCollapsedDepartmentId) return;
    this.route.params.subscribe(async (params) => {
      this.bottomSheet.dismiss();
    });
    this.bottomSheet.open(PreviousJobsLogSheetComponent, {
      data: {
        user: user,
        departmentId: currentCollapsedDepartmentId,
      },
    });
  }

  trackByDepartmentId(index: number, object: DepartmentGroup): string {
    return object.department.id;
  }

  trackById(index: number, object: AvailabilityListUser | PoolerUser): string {
    return object._id;
  }

  trackByDate(index: number, availability: UserAvailability): string {
    return availability.date;
  }

  formatUserName(user: User): string {
    return formatUserName(user, this.customer$.value);
  }

  getAvailabilityBadgeTooltip(
    user: AvailabilityListUser,
    departmentGroup: DepartmentGroup,
    shiftEnum: ShiftEnum,
  ): string {
    let jobTypesString = '';

    user.jobTypes?.forEach((jobType) => {
      jobTypesString += this.i18n.jobTypeEnum[jobType] + ', ';
    });
    jobTypesString = jobTypesString.slice(0, -2);

    return !departmentGroup?.hasShifts?.[shiftEnum]
      ? this.i18n.shiftNotAvailableOnDepartment ||
          'Schicht nicht verfügbar bei dieser Abteilung'
      : (this.i18n.available || 'verfügbar') + ' - ' + jobTypesString;
  }
}
